import React from 'react';
import type {useNode} from '@craftjs/core';
import {Bars3BottomLeftIcon, Bars3BottomRightIcon, Bars3Icon} from '@heroicons/react/24/outline';
import {ColorPicker, Divider, InputSlider} from 'components';
import {classNames, formatColorResult, suffixRemove} from 'utils';
import {useEditorStyle} from 'hooks';

import EditorInspector from 'components/app/editor/inspectors/Editor.Inspector';
import EditorInspectorPadding from 'components/app/editor/inspectors/Editor.Inspector.Padding';
import EditorInspectorMargin from 'components/app/editor/inspectors/Editor.Inspector.Margin';

interface IEditorInspectorParagraphProps {}

const EditorInspectorParagraph = ({}: IEditorInspectorParagraphProps) => {
  return (
    <EditorInspector title="Paragraph">
      {props => {
        return (
          <>
            <TextTypography {...props} />
            <Divider />
            <EditorInspectorPadding {...props} />
            <Divider />
            <EditorInspectorMargin {...props} />
          </>
        );
      }}
    </EditorInspector>
  );
};

export default React.memo(EditorInspectorParagraph);

interface IEditProps {
  style: React.CSSProperties | undefined;
  setProp: ReturnType<typeof useNode>['actions']['setProp'];
}

const TextTypography = ({style, setProp}: IEditProps) => {
  const alignments = [
    {textAlign: 'left', text: 'Left', icon: Bars3BottomLeftIcon},
    {textAlign: 'center', text: 'Center', icon: Bars3Icon},
    {textAlign: 'right', text: 'Right', icon: Bars3BottomRightIcon},
  ];
  const {updateStyle} = useEditorStyle({setProp});
  return (
    <div className="pb-2">
      <h4 className="font-medium text-sm text-slate-800 dark:text-white pb-6">Typography</h4>
      <div className="flex flex-col gap-5">
        <div className="flex items-center justify-between">
          <p className="text-sm">Color</p>
          <ColorPicker
            alignment="right"
            color={style?.color ?? 'black'}
            onPicked={color => {
              updateStyle('color', formatColorResult(color));
            }}
          />
        </div>
        <div className="flex items-center justify-between">
          <p className="text-sm">Align</p>
          <div className="flex items-center text-slate-800 dark:text-darkText border divide-x rounded-xs">
            {alignments.map(alignment => {
              return (
                <button
                  type="button"
                  key={alignment.textAlign}
                  className="cursor-pointer p-0.5 hover:brightness-105 dark:hover:brightness-120"
                  onClick={() => {
                    updateStyle('textAlign', alignment.textAlign);
                  }}>
                  <alignment.icon className={classNames('size-6 cursor-pointer', style?.textAlign === alignment.textAlign && 'text-primary')} />
                </button>
              );
            })}
          </div>
        </div>
        <InputSlider
          label="Font Size"
          suffix="px"
          defaultValue={suffixRemove('px', style?.fontSize as string)}
          min={12}
          max={160}
          onSlide={value => {
            updateStyle('fontSize', `${value}px`);
          }}
        />
      </div>
    </div>
  );
};
